import { useCallback, useEffect, useRef, useState } from "react";

import { extractCursor } from "@/redux/api";
import type { NotificationResponse } from "@/redux/notifications/types";
import type { CursorPage } from "@/redux/types";
import {
  useBulkMarkNotificationsReadMutation,
  useGetUnreadNotificationsCountQuery,
  useLazyGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
} from "@/redux/service.js";
import type {
  UseNotificationsFeedArgs,
  UseNotificationsFeedReturn,
} from "../../types";

const sortUnreadFirst = (notifications: NotificationResponse[]) =>
  [...notifications].sort((a, b) => {
    if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
    return 0;
  });

export const useNotificationsFeed = ({
  enabled,
  pollingInterval = 30000,
}: UseNotificationsFeedArgs): UseNotificationsFeedReturn => {
  const [items, setItems] = useState<NotificationResponse[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const itemsRef = useRef<NotificationResponse[]>([]);

  const { data: countData } = useGetUnreadNotificationsCountQuery(undefined, {
    pollingInterval,
  });
  const unreadCount = countData?.count ?? 0;

  const [triggerFetch, { isFetching }] = useLazyGetNotificationsQuery();
  const [bulkMarkRead] = useBulkMarkNotificationsReadMutation();
  const [markAllReadMutation] = useMarkAllNotificationsReadMutation();

  const updateItems = useCallback((next: NotificationResponse[]) => {
    const sorted = sortUnreadFirst(next);
    itemsRef.current = sorted;
    setItems(sorted);
  }, []);

  const fetchPage = useCallback(
    async (cursor?: string) => {
      const result: CursorPage<NotificationResponse> = await triggerFetch({
        cursor,
      }).unwrap();
      updateItems(
        cursor ? [...itemsRef.current, ...result.results] : result.results,
      );
      setNextCursor(extractCursor(result.next));
    },
    [triggerFetch, updateItems],
  );

  useEffect(() => {
    if (enabled) {
      fetchPage();
      return;
    }
    const unreadIds = itemsRef.current
      .filter((n) => !n.isRead)
      .map((n) => n.id);
    if (unreadIds.length > 0) {
      bulkMarkRead({ ids: unreadIds });
    }
    itemsRef.current = [];
    setItems([]);
    setNextCursor(null);
  }, [enabled]);

  const loadMore = useCallback(() => {
    if (isFetching || !nextCursor) return;
    fetchPage(nextCursor);
  }, [isFetching, nextCursor, fetchPage]);

  const refresh = useCallback(async () => {
    await fetchPage();
  }, [fetchPage]);

  const markAllRead = useCallback(async () => {
    await markAllReadMutation().unwrap();
    const marked = itemsRef.current.map((i) => ({ ...i, isRead: true }));
    itemsRef.current = marked;
    setItems(marked);
  }, [markAllReadMutation]);

  return {
    items,
    unreadCount,
    hasMore: Boolean(nextCursor),
    isFetching,
    loadMore,
    refresh,
    markAllRead,
  };
};
